import {
  ButtonOptionsMessage as ButtonOptionsMessageType,
  Conversation,
} from '@stripe-internal/conversations-client';
import {useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {ChatButtonOptions} from '../../../Chat';
import {messages} from './messages';

type Props = {
  conversation: Conversation;
  message: ButtonOptionsMessageType;
  animated?: boolean;
  disableInteraction?: boolean;
};

export const ButtonOptionsMessage = ({
  conversation,
  message,
  animated,
  disableInteraction,
}: Props) => {
  const [selectedOptionId, setSelectedOptionId] = useState<string | undefined>(
    message.selectedOptionId || undefined,
  );
  const [error, setError] = useState<React.ReactNode | null>(null);

  if (selectedOptionId === 'get-smart-suggestions') {
    // we're already returning null in the wrapping MessageAddedRow, but this
    // is an additional safeguard against rendering anything here when we've
    // replaced the current prompt with new options via 'get-smart-suggestions'
    return null;
  }

  const options = message.options
    // text-input is a special case when the user chooses to input text
    // manually instead of choosing an option. we represent this as a
    // test-input selected option, but don't want to render it as
    // an option.
    .filter((option) => option.id !== 'text-input')
    .map((option) => {
      return {
        ...option,
        value: option.id,
        onPress: async () => {
          setSelectedOptionId(option.id);
          setError(null);

          const resp = await conversation.selectOption({
            message,
            selectedOptionId: option.id,
          });

          if ('error' in resp) {
            setError(resp.error);
            setSelectedOptionId(undefined);
          } else if (resp.code === 205) {
            setSelectedOptionId(undefined);
          } else {
            setSelectedOptionId(option.id);
          }
        },
      };
    });

  return (
    <ChatButtonOptions
      key={`buttons-options-message-${message.id}`}
      options={options}
      disabled={disableInteraction}
      selectedOptionId={message.selectedOptionId ?? selectedOptionId}
      animated={animated}
      error={error ? <FormattedMessage {...messages.sendError} /> : null}
    />
  );
};
